/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Tooltip,
  TextField,
  Card,
  CardContent,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridPinnedColumnFields,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import BackdropProgressBar from "../../../components/ui/BackdropProgressBar";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import {
  dataGridHeaderHeight,
  dataGridRowHeight,
  GRIDCOLUMNKEYS,
  SEARCHBOX,
  SEVERITIES,
} from "../../../data/constants";
import {
  EDITRESOURCEMESSAGES,
  GLOBALMESSAGES,
  MANAGESETTING_TOOLTIPMESSAGES,
  MANAGESETTINGS,
} from "../../../constants/messages";
import useApi from "../../../hooks/useApi";
import { usePersistedColumnVisibility } from "../../../hooks/usePersistedColumnVisibility";
import { useUser } from "../../../context/UserContext";
import {
  getAllPortalSystemSettings,
  updateSystemSettingName,
  updateSystemSettingNew,
} from "../../../services/systemSettingService";
import {
  SettingGridRow,
  SystemSettingNameDto,
  SystemSettingNewDto,
  systemSettingResponseDto,
} from "../../../types/Setting";
import { useToaster } from "../../../hooks/useToaster";
import { filterDataBySearch } from "../../../utils/commonHelper";
import { useSettingsHub } from "../../../hooks/useSettingsHub";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/** Display names for the internally detected data type, used only when the
 * backend has no FieldType of its own (i.e. 'New'-sourced settings). */
const DATATYPE_LABELS: Record<SettingGridRow["dataType"], string> = {
  number: "Number",
  json: "JSON",
  options: "Single Choice",
  text: "Text",
};

/** Prefer the authoritative System_Setting_Names.FieldType, read off the row's
 * originalDto rather than a top-level row property - filterDataBySearch scans
 * every own property, so hoisting FieldType onto the row would make the search
 * box match on a value no column displays. Falls back to the detected type so
 * the field is never blank for 'New'-sourced settings, which have no FieldType. */
const resolveDataTypeLabel = (row: SettingGridRow | null): string => {
  if (!row) return "";
  const backendType =
    row.source === "Name"
      ? (row.originalDto as SystemSettingNameDto | undefined)?.fieldType
      : undefined;
  return backendType?.trim() || DATATYPE_LABELS[row.dataType] || "";
};

const ManageSettings: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRow, setCurrentRow] = useState<SettingGridRow | null>(null);
  const { loading, executeApi } = useApi();
  const { userName } = useUser();
  const showToaster = useToaster();
  const [allData, setAllData] = useState<SettingGridRow[]>([]);
  const [errors, setErrors] = useState({
    value: "",
    defaultValue: "",
    description: "",
  });
  const apiRef = useGridApiRef();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { sendSettingChanged } = useSettingsHub();

  const mapNewDtos = (items: SystemSettingNewDto[] = []): SettingGridRow[] =>
    items.map((x) => {
      const rawVal = x.value ?? "";
      const { dataType, options, normalizedValue, normalizedDefaultValue } =
        detectTypeAndOptions(rawVal, null, null, x.defaultValue) ?? {};

      return {
        id: x.id ?? null,
        uniqueId: `new-${x.id ?? x.property ?? crypto.randomUUID()}`,
        property: x.property ?? "",
        value: normalizedValue ?? "",
        defaultValue: normalizedDefaultValue ?? "",
        description: x.description ?? "",
        source: "New",
        dataType,
        options,
      };
    });

  const mapNameDtos = (items: SystemSettingNameDto[] = []): SettingGridRow[] =>
    items.map((x) => {
      const val = x.systemSettingValues?.[0]?.settingValue ?? "";
      const { dataType, options, normalizedValue, normalizedDefaultValue } =
        detectTypeAndOptions(val, x.fieldType, x.allowedValues, x.defaultValue) ?? {};
      return {
        id: x.id,
        uniqueId: `name-${x.id}`,
        property: x.token ?? "",
        value: normalizedValue ?? "",
        defaultValue: normalizedDefaultValue ?? "",
        description: x.settingTitle ?? "",
        source: "Name",
        dataType,
        options,
        originalDto: x,
      };
    });

  const [pageState, setPageState] = useState<{ data: SettingGridRow[] | null; total: number }>({
    data: [],
    total: 0,
  });

  const fetchSystemSettings = useCallback(async () => {
    const allSettings = await executeApi<void, systemSettingResponseDto>(
      getAllPortalSystemSettings,
      null,
    );

    if (!allSettings) {
      setPageState({
        data: [],
        total: 0,
      });
      return;
    }

    const rows: SettingGridRow[] = [
      ...mapNewDtos(allSettings.systemSettingsNew ?? []),
      ...mapNameDtos(allSettings.systemSettingNames ?? []),
    ];

    setAllData(rows ?? []);

    setPageState({
      data: rows,
      total: rows && rows?.length,
    });
  }, []);

  useEffect(() => {
    fetchSystemSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSystemSettings]);

  useEffect(() => {
    if (searchText) {
      const lowerText = searchText.toLowerCase();
      const filtered = filterDataBySearch(allData, lowerText);

      setPageState({
        data: filtered ?? [],
        total: filtered?.length,
      });
    } else {
      setPageState({
        data: allData ?? [],
        total: allData?.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSearchText = (value: string) => setSearchText(value);

  const handleEditClick = (row: SettingGridRow) => {
    setCurrentRow({ ...row });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrors({
      value: "",
      defaultValue: "",
      description: "",
    });
    setCurrentRow(null);
  };

  const handleSave = async () => {
    if (!currentRow) return;
    // validation
    if (!validateAll()) return;

    const originalRow = pageState.data?.find((row) => row.uniqueId === currentRow.uniqueId);
    if (
      originalRow &&
      originalRow.value === currentRow.value &&
      originalRow.defaultValue === currentRow.defaultValue &&
      originalRow.description === currentRow.description
    ) {
      showToaster(EDITRESOURCEMESSAGES.NO_UPDATE, SEVERITIES.INFO);
      return;
    }

    if (currentRow.source === "New") {
      const systemSettingNew: SystemSettingNewDto = {
        id: currentRow.id,
        description: currentRow.description,
        value: currentRow.value,
        defaultValue: currentRow.defaultValue,
        uniqueId: currentRow.id?.toString(),
      };
      await executeApi<SystemSettingNewDto, number>(updateSystemSettingNew, systemSettingNew);
    } else {
      const nameDto = currentRow.originalDto as SystemSettingNameDto;
      nameDto.defaultValue = currentRow.defaultValue;
      nameDto.settingTitle = currentRow.description;
      nameDto.systemSettingValues[0].settingValue = currentRow.value;

      await executeApi<SystemSettingNameDto, number>(updateSystemSettingName, nameDto);
    }

    setPageState((prev) => {
      if (!prev.data) return prev;

      const updated = prev.data.map((row) =>
        row.uniqueId === currentRow.uniqueId ? { ...currentRow } : row,
      );

      return {
        ...prev,
        data: updated,
      };
    });
    showToaster(MANAGESETTINGS.SETTING_UPDATE_SUCCESS, SEVERITIES.SUCCESS);
    await sendSettingChanged(userName, currentRow.property);
    handleCloseDialog();
  };

  const validateField = (
    field: "value" | "defaultValue" | "description",
    val: string,
    dataType?: string,
    options?: string[],
  ) => {
    let err = "";

    if (field === "description") {
      if (!val?.trim()) err = MANAGESETTINGS.DESCRIPTION_EMPTY;
      return err;
    }

    if (dataType === "number") {
      if (!val?.trim() || isNaN(Number(val))) err = MANAGESETTINGS.VALID_NUMBER;
    } else if (dataType === "json") {
      try {
        if (!val?.trim()) throw new Error();
        JSON.parse(val);
      } catch {
        err = MANAGESETTINGS.VALID_JSON;
      }
    } else if (dataType === "options") {
      if (!val?.trim() || !options?.some((o) => o.toLowerCase() === val.toLowerCase())) {
        err = MANAGESETTINGS.VALID_OPTION;
      }
    } else {
      if (!val?.trim()) err = MANAGESETTINGS.FIELD_EMPTY;
    }

    return err;
  };

  const validateAll = () => {
    if (!currentRow) return false;

    const newErrors = {
      value: validateField(
        "value",
        currentRow.value ?? "",
        currentRow.dataType,
        currentRow.options,
      ),
      defaultValue: validateField(
        "defaultValue",
        currentRow.defaultValue ?? "",
        currentRow.dataType,
        currentRow.options,
      ),
      description: validateField("description", currentRow.description ?? "", currentRow.dataType),
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some(Boolean);
  };

  const columns: GridColDef[] = [
    { field: "property", headerName: "Property", flex: 1, minWidth: 200 },
    { field: "value", headerName: "Value", flex: 1, minWidth: 150 },
    { field: "defaultValue", headerName: "Default Value", flex: 1, minWidth: 150 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 200 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      hideable: false,
      filterable: false,
      disableExport: true,
      disableColumnMenu: false,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton onClick={() => handleEditClick(params.row)}>
            <EditIcon id="editSetting" fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const [pinnedColumns, setPinnedColumns] = React.useState<GridPinnedColumnFields>({
    right: ["actions"],
  });

  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.MANAGE_SETTINGS
  );

  const detectTypeAndOptions = (
    value: string | null | undefined,
    fieldType?: string,
    allowedValues?: string,
    defaultValue?: string | null | undefined,
  ): {
    dataType: "number" | "json" | "options" | "text";
    options?: string[];
    normalizedValue?: string;
    normalizedDefaultValue?: string;
  } => {
    const v = value?.trim();
    const dv = defaultValue?.trim();

    // 1. Single Choice → dropdown with allowed values
    if (fieldType === "Single Choice") {
      const opts = allowedValues?.split(",").map((o) => o.trim()) ?? [];
      const findMatch = (val?: string) =>
        opts.find((opt) => opt.toLowerCase() === val?.toLowerCase()) ?? "";

      return {
        dataType: "options",
        options: opts,
        normalizedValue: v ? findMatch(v) : "",
        normalizedDefaultValue: dv ? findMatch(dv) : "",
      };
    }

    // 2. Number
    if (v && !isNaN(Number(v))) {
      return { dataType: "number", normalizedValue: v, normalizedDefaultValue: dv ?? "" };
    }

    // 3. JSON
    try {
      if (v && typeof JSON.parse(v) === "object") {
        return { dataType: "json", normalizedValue: v, normalizedDefaultValue: dv ?? "" };
      }
    } catch {
      // Ignore JSON parse errors
    }

    // 4. Boolean-like options
    const processBooleanLike = (val?: string) => {
      if (!val) return "";
      const lower = val.toLowerCase();

      if (["yes", "no"].includes(lower)) {
        const opts = ["Yes", "No"];
        return opts.find((opt) => opt.toLowerCase() === lower) ?? val;
      }

      if (["true", "false"].includes(lower)) {
        const opts = ["True", "False"];
        return opts.find((opt) => opt.toLowerCase() === lower) ?? val;
      }

      return val;
    };

    const normalizedValue = processBooleanLike(v);
    const normalizedDefaultValue = processBooleanLike(dv);

    // 5. Set options if value is boolean-like
    const lowerV = v?.toLowerCase();
    let options: string[] | undefined;
    if (["yes", "no"].includes(lowerV ?? "")) options = ["Yes", "No"];
    else if (["true", "false"].includes(lowerV ?? "")) options = ["True", "False"];

    // 6. Fallback text
    const dataType: "number" | "json" | "options" | "text" = options ? "options" : "text";

    return { dataType, options, normalizedValue, normalizedDefaultValue };
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /** MUI's defaultGetRowsToExport narrows the export to the selected rows whenever
   * the selection is non-empty, so clicking a row before exporting silently reduced
   * the CSV to that row. Export every filtered/sorted row instead, so CSV matches
   * the PDF export, which always writes the full grid.
   *
   * allColumns is the same guarantee on the column axis: exportDataAsCsv otherwise
   * writes only the *visible* columns, and since bug 78034 made the "Manage columns"
   * selection persist, a column hidden once would have been missing from every later
   * CSV while the PDF still wrote it. The Actions column sets disableExport, so
   * allColumns still excludes it. */
  const handleExportCsv = () => {
    apiRef.current.exportDataAsCsv({
      fileName: MANAGESETTINGS.FILE_NAME,
      allColumns: true,
      getRowsToExport: ({ apiRef: gridApiRef }) => gridFilteredSortedRowIdsSelector(gridApiRef),
    });
  };

  const handleExportPdf = () => {
    if (!pageState.data || pageState.data.length === 0) return;
    const doc = new jsPDF("p");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Manage Settings", pageWidth / 2, 15, { align: "center" });

    const tableColumn = columns
      .filter(
        (col) => col.field === "property" || col.field === "value" || col.field === "description",
      )
      .map((col) => col.headerName || col.field);

    const tableRows = pageState.data.map((row) => [
      row.property ?? "",
      row.value ?? "",
      row.description ?? "",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      theme: "grid",
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 70 },
        2: { cellWidth: 70 },
      },
    });

    doc.save(`${MANAGESETTINGS.FILE_NAME}.pdf`);
  };

  return (
    <section className="mb-3">
      <Card>
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">
            Manage Settings
          </Typography>
          {<BackdropProgressBar loading={loading} showAbsolute={true} />}
          <Box component="form">
            <Grid2 container spacing={2}>
              <Grid2
                size={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "nowrap",
                }}
              >
                <SearchBox
                  searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
                  onSearch={handleSearchText}
                />

                <Button variant="contained" sx={{ ml: "auto" }} onClick={handleClick}>
                  {MANAGESETTINGS.EXPORT}
                </Button>
              </Grid2>
              <Grid2 size={12}>
                <div className="grid-height">
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                    <MenuItem
                      onClick={() => {
                        handleExportCsv();
                        handleClose();
                      }}
                    >
                      {MANAGESETTINGS.EXPORT_CSV}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleExportPdf();
                        handleClose();
                      }}
                    >
                      {MANAGESETTINGS.EXPORT_PRINT}
                    </MenuItem>
                  </Menu>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={pageState.data}
                    columns={columns}
                    columnHeaderHeight={dataGridHeaderHeight}
                    rowHeight={dataGridRowHeight}
                    getRowId={(row) => row.id}
                    hideFooterPagination
                    localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                  />
                </div>
              </Grid2>
            </Grid2>
          </Box>

          {/* Edit Dialog */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              <SettingsIcon /> Edit Setting
            </DialogTitle>

            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              className="dialog-close-button"
            >
              <CloseIcon />
            </IconButton>

            <DialogContent dividers>
              {/* Name */}
              <TextField
                id="lblName"
                label="Name"
                value={currentRow?.property || ""}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                size="small"
                disabled
              />

              {/* Data Type */}
              <TextField
                id="lblDataType"
                label="Data Type"
                value={resolveDataTypeLabel(currentRow)}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                size="small"
                disabled
              />

              {/* Value */}
              {currentRow?.dataType === "options" && currentRow?.options?.length ? (
                <TextField
                  id="lblValue"
                  select
                  label="Value"
                  value={currentRow.value || ""}
                  fullWidth
                  size="small"
                  margin="normal"
                  onChange={(e) => {
                    const v = e.target.value;
                    setCurrentRow({ ...currentRow, value: v });
                    setErrors((prev) => ({
                      ...prev,
                      value: validateField("value", v, currentRow?.dataType, currentRow.options),
                    }));
                  }}
                  error={!!errors.value}
                  helperText={errors.value}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {currentRow.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </TextField>
              ) : (
                <TextField
                  id="lblValue"
                  label="Value"
                  type={currentRow?.dataType === "number" ? "number" : "text"}
                  value={currentRow?.value || ""}
                  fullWidth
                  size="small"
                  margin="normal"
                  multiline={currentRow?.dataType === "json"}
                  minRows={currentRow?.dataType === "json" ? 3 : undefined}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCurrentRow({ ...currentRow, value: v });
                    setErrors((prev) => ({
                      ...prev,
                      value: validateField("value", v, currentRow?.dataType, currentRow?.options),
                    }));
                  }}
                  error={!!errors.value}
                  helperText={errors.value}
                  InputProps={{
                    endAdornment: currentRow &&
                      currentRow.value !==
                        pageState.data?.find((s) => s.id === currentRow.id)?.value && (
                        <Tooltip title={MANAGESETTING_TOOLTIPMESSAGES.ROLLBACK}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const originalValue =
                                pageState.data?.find((s) => s.id === currentRow.id)?.value || "";
                              setCurrentRow({ ...currentRow, value: originalValue });
                            }}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                  }}
                />
              )}

              {/* Default Value */}
              {currentRow?.dataType === "options" && currentRow?.options?.length ? (
                <TextField
                  id="lblDefaultValue"
                  select
                  label="Default Value"
                  value={currentRow?.defaultValue || ""}
                  fullWidth
                  size="small"
                  margin="normal"
                  onChange={(e) => {
                    const v = e.target.value;
                    setCurrentRow({ ...currentRow, defaultValue: v });
                    setErrors((prev) => ({
                      ...prev,
                      defaultValue: validateField(
                        "defaultValue",
                        v,
                        currentRow?.dataType,
                        currentRow?.options,
                      ),
                    }));
                  }}
                  error={!!errors.defaultValue}
                  helperText={errors.defaultValue}
                  SelectProps={{ native: true }}
                >
                  <option value="">Select</option>
                  {currentRow.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </TextField>
              ) : (
                <TextField
                  id="lblDefaultValue"
                  label="Default Value"
                  type={currentRow?.dataType === "number" ? "number" : "text"}
                  value={currentRow?.defaultValue || ""}
                  fullWidth
                  margin="normal"
                  onChange={(e) => {
                    const v = e.target.value;
                    setCurrentRow({ ...currentRow, defaultValue: v });
                    setErrors((prev) => ({
                      ...prev,
                      defaultValue: validateField(
                        "defaultValue",
                        v,
                        currentRow?.dataType,
                        currentRow?.options,
                      ),
                    }));
                  }}
                  error={!!errors.defaultValue}
                  helperText={errors.defaultValue}
                  placeholder="Default value"
                  size="small"
                  multiline={currentRow?.dataType === "json"}
                  minRows={currentRow?.dataType === "json" ? 3 : undefined}
                />
              )}

              {/* Description */}
              <TextField
                id="lblDescription"
                label="Description"
                value={currentRow?.description || ""}
                fullWidth
                multiline
                margin="normal"
                onChange={(e) => {
                  const v = e.target.value;
                  setCurrentRow({ ...currentRow, description: v });
                  setErrors((prev) => ({ ...prev, description: validateField("description", v) }));
                }}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Add a description"
                rows={3}
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseDialog} variant="contained" color="light">
                Cancel
              </Button>
              <Button id="btnSettingSave" variant="contained" onClick={handleSave} color="primary">
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </section>
  );
};

export default ManageSettings;
